import { useCallback, useMemo, useRef } from 'react';
import { type ProjectSnapshotDay } from '@forecastify/shared';
import { Button, DropdownMenu, Icon, Tooltip } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useT, type Translate } from '../../i18n';
import { changeLabel, useSnapshotMenu, type Lookback } from './use-lookback';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C62.4 · Visszatekintés. The snapshot timeline, rebuilt from the mockup.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS IS THE MOCKUP'S OWN ROW, ELEMENT FOR ELEMENT, AND THE VERSION BEFORE IT WAS BUILT
 * FROM A SENTENCE OF PROSE.** `PHASE-5.md` §2.3 says *"a scrubber with month markers"*, and a
 * scrubber with month markers is what got built — a slider over a continuum of dates, with a
 * calendar, a switch and a row of figures the mockup does not draw. The mockup's `ttVals` and its
 * markup say something much more specific, and all of it matters:
 *
 *  * **the track is a list of DAYS, not a range.** One tick per snapshot day, placed at its real
 *    date, so a collection gap is a visible hole. Dragging SNAPS to the nearest day;
 *  * **the ticks are not uniform.** A Monday is taller than a Tuesday, the first of a month taller
 *    still, and a day the plan changed is amber — so the eye finds the interesting days without
 *    opening anything;
 *  * **the date list is a menu with a search**, *Ugrás dátumra…*, carrying each day's running
 *    figure and a marker on the days the plan moved. Not a calendar: a calendar can offer a
 *    Saturday there is no snapshot for;
 *  * **the row carries nothing else.** No figures, no switch, no sentence. The delta and the way
 *    back live in the tab's kebab, which is where the mockup puts them.
 *
 * ⚠️ **`hint` differs per tab** — hours on the Dashboard, spend on Pénzügy — which is the
 * mockup's `kind` parameter, and why the caller supplies it.
 */
export function LookbackBar({
  lookback,
  hint,
}: {
  readonly lookback: Lookback;
  /** ⚠️ The running figure beside each day. Hours here, money on the finance tab. */
  readonly hint?: (day: ProjectSnapshotDay) => string;
}) {
  const t = useT();
  /* 3 Nov 2026 · the running hours in the reader's unit (`decisions.md` DU-1). */
  const { fmt } = useDuration();
  const track = useRef<HTMLDivElement | null>(null);

  const hintOf = useCallback(
    (day: ProjectSnapshotDay) => (hint === undefined ? fmt(day.loggedHours) : hint(day)),
    [hint, fmt],
  );
  const menu = useSnapshotMenu(lookback, hintOf);

  const { days, onDate } = lookback;
  const first = days[0]?.onDate ?? null;

  /**
   * ⚠️⚠️ **THE TICKS SIT AT THEIR REAL DATE, WHICH IS THE WHOLE POINT OF THE RULER.** Spacing them
   * evenly by index would draw a gap-free track over a month that lost three days to a collection
   * outage — the mockup's comment says it out loud: *"a tick-ek a valós dátum arányában ülnek, így
   * a kimaradások is látszanak"*.
   */
  const geometry = useMemo(() => {
    if (first === null) return null;
    const today = new Date().toISOString().slice(0, 10);
    const zero = Date.parse(`${first}T00:00:00.000Z`);
    const total = Math.max(1, (Date.parse(`${today}T00:00:00.000Z`) - zero) / 86_400_000);
    const pctOf = (day: string): number =>
      Math.min(
        100,
        Math.max(0, ((Date.parse(`${day}T00:00:00.000Z`) - zero) / 86_400_000 / total) * 100),
      );

    const names = t('calendar.months').split(',');
    const months: { key: string; left: number; label: string }[] = [];
    const cursor = new Date(`${first.slice(0, 8)}01T00:00:00.000Z`);
    if (cursor.toISOString().slice(0, 10) < first) cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    for (let guard = 0; guard < 40; guard++) {
      const day = cursor.toISOString().slice(0, 10);
      if (day > today) break;
      months.push({ key: day, left: pctOf(day), label: names[cursor.getUTCMonth()] ?? '' });
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }

    const ticks = days.map((day) => {
      const monday = new Date(`${day.onDate}T00:00:00.000Z`).getUTCDay() === 1;
      const opening = day.onDate.slice(8) === '01';
      const changed = day.changes.length > 0;
      return {
        key: day.onDate,
        left: pctOf(day.onDate),
        /* ⚠️ The mockup's four heights exactly: a changed day, the first of a month, a Monday,
           and everything else. */
        height: changed ? 15 : opening ? 17 : monday ? 12 : 8,
        color: changed
          ? 'var(--status-warn)'
          : monday || opening
            ? 'color-mix(in oklch, var(--foreground) 34%, transparent)'
            : 'color-mix(in oklch, var(--foreground) 18%, transparent)',
        title: dayTitle(t, fmt, day),
      };
    });

    return { pctOf, months, ticks };
  }, [days, first, t, fmt]);

  /**
   * ⚠️⚠️ **A DRAG LANDS ON THE NEAREST SNAPSHOT DAY, NEVER BETWEEN TWO.** The pointer moves over a
   * continuum and the data does not: picking the closest day is what makes the control honest
   * about a gap instead of quietly answering with whichever neighbour rounds first.
   */
  const pickAt = useCallback(
    (clientX: number): void => {
      const box = track.current === null ? null : track.current.getBoundingClientRect();
      if (box === null || box.width === 0 || first === null) return;
      const share = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
      const today = new Date().toISOString().slice(0, 10);
      const zero = Date.parse(`${first}T00:00:00.000Z`);
      const span = Math.max(1, (Date.parse(`${today}T00:00:00.000Z`) - zero) / 86_400_000);
      const target = zero + Math.round(share * span) * 86_400_000;

      let best: ProjectSnapshotDay | undefined;
      let distance = Number.POSITIVE_INFINITY;
      for (const day of days) {
        const gap = Math.abs(Date.parse(`${day.onDate}T00:00:00.000Z`) - target);
        if (gap < distance) {
          distance = gap;
          best = day;
        }
      }
      if (best !== undefined) lookback.go(best.onDate);
    },
    [days, first, lookback],
  );

  if (onDate === null || geometry === null) return null;

  return (
    <div className="bg-muted/25 flex items-center gap-2 border-b px-4 py-1">
      {/* ⚠️ 150px and mono, from the mockup — a fixed width so the track does not jump a pixel
          left and right as the date's word length changes under a drag. */}
      <span className="w-[150px] shrink-0 font-mono text-[12px] leading-tight font-medium whitespace-nowrap tabular-nums">
        {longDate(t, onDate)}
      </span>

      <div
        ref={track}
        role="slider"
        tabIndex={0}
        aria-label={t('tt.track')}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, days.length - 1)}
        aria-valuenow={days.findIndex((one) => one.onDate === onDate)}
        aria-valuetext={onDate}
        className="focus-visible:ring-ring/50 relative h-[44px] min-w-0 flex-1 cursor-ew-resize rounded-sm outline-none select-none focus-visible:ring-[3px]"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          pickAt(event.clientX);
        }}
        onPointerMove={(event) => {
          /* ⚠️ `buttons` rather than a dragging flag: the pointer is captured, so a move with no
             button down is a hover the control must ignore. */
          if (event.buttons !== 0) pickAt(event.clientX);
        }}
        onKeyDown={(event) => {
          const by =
            event.key === 'ArrowLeft' || event.key === 'ArrowDown'
              ? -1
              : event.key === 'ArrowRight' || event.key === 'ArrowUp'
                ? 1
                : event.key === 'PageDown'
                  ? -5
                  : event.key === 'PageUp'
                    ? 5
                    : 0;
          if (by === 0) return;
          event.preventDefault();
          lookback.step(by);
        }}
      >
        {geometry.months.map((month) => (
          <span
            key={month.key}
            className="text-muted-foreground/60 absolute top-[2px] text-[10px] font-medium tracking-wide uppercase"
            style={{ left: `${String(month.left)}%` }}
          >
            {month.label}
          </span>
        ))}

        <span className="bg-border absolute bottom-[10px] left-0 h-px w-full" />

        {geometry.ticks.map((tick) => (
          <Tooltip key={tick.key} asChild content={tick.title}>
            <span
              className="absolute bottom-[10px] w-px"
              style={{
                left: `${String(tick.left)}%`,
                height: `${String(tick.height)}px`,
                background: tick.color,
              }}
            />
          </Tooltip>
        ))}

        <span
          className="bg-primary absolute bottom-[10px] h-[26px] w-[2px]"
          style={{ left: `${String(geometry.pctOf(onDate))}%` }}
        />
        <span
          className="bg-primary absolute bottom-[34px] size-[7px] rounded-full"
          style={{ left: `${String(geometry.pctOf(onDate))}%`, transform: 'translateX(-2.5px)' }}
        />

        <span className="text-muted-foreground/70 absolute right-0 bottom-0 text-[10px] font-medium tracking-wide uppercase">
          {t('tt.present')}
        </span>
      </div>

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('tt.prev')}
        disabled={lookback.atFirst}
        onClick={() => {
          lookback.step(-1);
        }}
      >
        <Icon name="chevron-left" size={14} />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('tt.next')}
        disabled={lookback.atLast}
        onClick={() => {
          lookback.step(1);
        }}
      >
        <Icon name="chevron-right" size={14} />
      </Button>

      {/**
       * ⚠️⚠️ **A SEARCHABLE LIST OF DAYS, NOT A CALENDAR.** *Ugrás dátumra…* over the days that
       * exist — a calendar would offer a Saturday, a public holiday and tomorrow, none of which
       * has a snapshot, and would then have to refuse three quarters of what it drew.
       */}
      <DropdownMenu
        width={320}
        align="end"
        label={t('tt.pick')}
        searchPlaceholder={t('tt.search')}
        emptyLabel={t('tt.searchEmpty')}
        items={menu}
      >
        <Button variant="ghost" size="icon-sm" aria-label={t('tt.pick')}>
          <Icon name="calendar-days" size={14} />
        </Button>
      </DropdownMenu>

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('tt.back')}
        onClick={() => {
          lookback.end();
        }}
      >
        <Icon name="x" size={14} />
      </Button>
    </div>
  );
}

/** ⚠️ `2026. június 24.` — the mockup's `ttDateLong`, built from the catalogue's month names. */
function longDate(t: Translate, onDate: string): string {
  const months = t('calendar.months').split(',');
  return `${onDate.slice(0, 4)}. ${months[Number(onDate.slice(5, 7)) - 1] ?? ''} ${String(Number(onDate.slice(8)))}.`;
}

/** The tick's hover text: the day, its running figure, and what moved on it. */
function dayTitle(
  t: Translate,
  fmt: (hours: number | string) => string,
  day: ProjectSnapshotDay,
): string {
  const change = changeLabel(t, day.changes);
  const head = `${longDate(t, day.onDate)} · ${fmt(day.loggedHours)}`;
  return change === null ? head : `${head} · ${change}`;
}
